/**
 * BlogStudio — generate, refine, and publish HidroVerse blog posts
 * straight from the Content Manager (no redeploy required).
 *
 * Flow:
 *   1. Admin clicks "New post" → fills brief → AI drafts.
 *   2. Inline editor for title/excerpt/tags/body (HTML).
 *   3. Refine bar (instruction → AI rewrites).
 *   4. Revision history with one-click restore.
 *   5. Publish → appears on /insights immediately.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, Trash2, Eye, EyeOff, ExternalLink, RefreshCw, Sparkles } from "lucide-react";
import {
  StatusBadge, RefineBar, RevisionDrawer, BriefField,
  useAdminToken, studioFetch,
} from "../components/studio/_shared";
import ContentPlannerLayout from "../components/layout/ContentPlannerLayout";
import { MicField } from "../components/studio/MicField";

const CATEGORIES = [
  { value: "compliance",  label: "Compliance & regulation" },
  { value: "treatment",   label: "Treatment technology" },
  { value: "pumps",       label: "Pumps & pressure" },
  { value: "wastewater",  label: "Wastewater & reuse" },
  { value: "industry",    label: "Industry insights" },
  { value: "climate",     label: "Climate & trends" },
];

const LENGTHS = [
  { value: "short",  label: "Short — ~500 words" },
  { value: "medium", label: "Medium — ~900 words" },
  { value: "long",   label: "Long — ~1,500 words" },
];

const AUDIENCES = [
  "Lodge GMs, Municipal Engineers, Procurement Officers",
  "Lodge GMs",
  "Municipal Engineers",
  "Procurement Officers",
  "Estate trustees & managers",
  "General — water-curious public",
];

export default function BlogStudio() {
  const navigate = useNavigate();
  const token = useAdminToken();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [creatingBrief, setCreatingBrief] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login", { replace: true });
    }
  }, [token, navigate]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const d = await studioFetch("/api/content-studio/posts", { token });
      setPosts(d.posts || []);
      // auto-select first unpublished when nothing chosen
      if (!selectedId && d.posts && d.posts.length > 0) {
        setSelectedId(d.posts[0].id);
      }
    } catch (e) {
      if (/401|403/.test(e.message)) {
        localStorage.removeItem("hv_admin_token");
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, selectedId, navigate]);

  useEffect(() => { load(); }, [load]);

  const selected = posts.find((p) => p.id === selectedId);

  const draftNew = async (brief) => {
    const d = await studioFetch("/api/content-studio/posts/draft", {
      method: "POST", body: brief, token,
    });
    await load();
    setSelectedId(d.id);
    setCreatingBrief(false);
  };

  const removePost = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    await studioFetch(`/api/content-studio/posts/${id}`, { method: "DELETE", token });
    if (selectedId === id) setSelectedId(null);
    await load();
  };

  return (
    <ContentPlannerLayout>
      <Helmet>
        <title>Blog Studio — HidroVerse</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div data-testid="blog-studio">

        {error && (
          <div className="mb-4 px-4 py-3"
               style={{ background: "rgba(184,92,56,0.10)",
                        border: "1px solid rgba(184,92,56,0.35)",
                        color: "#B85C38", fontSize: "0.85rem",
                        borderRadius: 10 }}
               data-testid="blog-studio-error">
            {error}
          </div>
        )}

        <div className="grid gap-5"
             style={{ gridTemplateColumns: "minmax(0, 280px) minmax(0, 1fr)" }}>
          {/* ─── Left sidebar: list ───────────────────────────────── */}
          <aside className="space-y-3">
            <button type="button"
                    onClick={() => setCreatingBrief(true)}
                    data-testid="blog-new-post"
                    className="hv-btn w-full justify-center">
              <Plus size={13} /> New post
            </button>
            {loading && (
              <div className="font-mono uppercase"
                   style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                            color: "rgba(12,42,64,0.55)" }}>
                Loading…
              </div>
            )}
            {!loading && posts.length === 0 && (
              <div className="hv-card-soft" style={{ padding: 14 }}>
                <div className="font-light text-center"
                     style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.65)" }}>
                  No posts yet — click <b>New post</b>.
                </div>
              </div>
            )}
            <ul className="space-y-2"
                data-testid="blog-post-list">
              {posts.map((p) => (
                <li key={p.id}>
                  <button type="button"
                          onClick={() => setSelectedId(p.id)}
                          data-testid={`blog-post-item-${p.id}`}
                          className="w-full text-left p-3"
                          style={{
                            background: selectedId === p.id ? "rgba(31,182,193,0.12)" : "#FFFFFF",
                            border: `1px solid ${selectedId === p.id ? "rgba(31,182,193,0.45)" : "rgba(12,42,64,0.10)"}`,
                            borderRadius: 10, cursor: "pointer",
                          }}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <FileText size={12} style={{ color: "#0E7A82" }} />
                      <StatusBadge status={p.status} />
                    </div>
                    <div style={{ fontSize: "0.88rem", fontWeight: 600,
                                  lineHeight: 1.3 }}>
                      {p.title}
                    </div>
                    <div className="font-mono mt-1"
                         style={{ fontSize: "0.6rem",
                                  color: "rgba(12,42,64,0.55)" }}>
                      {(p.tags || []).slice(0, 3).join(" · ")}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* ─── Right pane: editor ──────────────────────────────── */}
          <section>
            {creatingBrief && (
              <BlogBriefForm
                onCancel={() => setCreatingBrief(false)}
                onSubmit={draftNew}
                apiToken={token}
              />
            )}
            {!creatingBrief && !selected && (
              <div className="hv-card-soft p-8 text-center"
                   data-testid="blog-empty">
                <Sparkles size={20} style={{ color: "#1FB6C1", margin: "0 auto" }} />
                <h3 className="mt-3" style={{ fontSize: "1rem", fontWeight: 600 }}>
                  No post selected
                </h3>
                <p className="mt-2 font-light max-w-md mx-auto"
                   style={{ fontSize: "0.88rem",
                            color: "rgba(12,42,64,0.65)", lineHeight: 1.55 }}>
                  Pick a post from the list, or click <b>New post</b> to brief
                  the AI on something fresh.
                </p>
              </div>
            )}
            {!creatingBrief && selected && (
              <BlogEditor
                post={selected}
                token={token}
                onUpdated={load}
                onDeleted={() => removePost(selected.id)}
              />
            )}
          </section>
        </div>
      </div>
    </ContentPlannerLayout>
  );
}

/* ───────────────────────────────────────────────────────────── */
/* Brief form                                                    */
/* ───────────────────────────────────────────────────────────── */
function BlogBriefForm({ onCancel, onSubmit, apiToken }) {
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState(AUDIENCES[0]);
  const [length, setLength] = useState("medium");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!topic.trim()) { setErr("Tell the AI what the post is about."); return; }
    setBusy(true); setErr("");
    try {
      await onSubmit({
        title: title.trim() || "",
        topic: topic.trim(),
        audience,
        length,
        tags: tags.split(/\s*,\s*/).filter(Boolean),
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hv-card-soft" style={{ padding: 20 }}
         data-testid="blog-brief-form">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono uppercase"
               style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                        color: "#0E7A82", fontWeight: 700 }}>
            Brief the AI
          </div>
          <h2 className="mt-1" style={{ fontSize: "1.15rem", fontWeight: 600 }}>
            What do you want to write about?
          </h2>
        </div>
        <button type="button" onClick={onCancel}
                disabled={busy}
                data-testid="blog-brief-cancel"
                className="hv-pill">
          Cancel
        </button>
      </div>

      <BriefField label="Title (or leave blank — AI will suggest one)" testid="blog-brief-title">
        <MicField value={title} onChange={setTitle} apiToken={apiToken}
                  testid="blog-brief-title-mic">
          <input type="text" value={title}
                 onChange={(e) => setTitle(e.target.value)}
                 data-testid="blog-brief-title-input"
                 className="hv-input w-full"
                 placeholder="(blank = AI suggests one)" />
        </MicField>
      </BriefField>

      <BriefField label="What's it about?"
                  hint="1–3 sentences. The more specific (the lodge problem, the standard, the engineering point) the better."
                  testid="blog-brief-topic">
        <MicField value={topic} onChange={setTopic} apiToken={apiToken}
                  testid="blog-brief-topic-mic">
          <textarea value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    rows={4}
                    data-testid="blog-brief-topic-input"
                    className="hv-input w-full"
                    style={{ resize: "vertical" }}
                    placeholder="e.g. 'How borehole yield drops 30–40% from the test-day figure to the long-term sustainable rate, why that breaks lodges on gala Saturdays, and the SANS 10299-4 fix.'" />
        </MicField>
      </BriefField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <BriefField label="Audience" testid="blog-brief-audience">
          <select value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  data-testid="blog-brief-audience-input"
                  className="hv-input w-full">
            {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </BriefField>
        <BriefField label="Length" testid="blog-brief-length">
          <select value={length}
                  onChange={(e) => setLength(e.target.value)}
                  data-testid="blog-brief-length-input"
                  className="hv-input w-full">
            {LENGTHS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </BriefField>
      </div>

      <BriefField label="Tags (comma-separated, optional)" testid="blog-brief-tags">
        <input type="text" value={tags}
               onChange={(e) => setTags(e.target.value)}
               data-testid="blog-brief-tags-input"
               className="hv-input w-full"
               placeholder="e.g. SANS 241, Borehole, Lodge" />
      </BriefField>

      {err && (
        <div className="my-2 font-mono"
             style={{ fontSize: "0.78rem", color: "#B85C38" }}
             data-testid="blog-brief-error">
          {err}
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button type="button" onClick={onCancel}
                disabled={busy} className="hv-pill">
          Cancel
        </button>
        <button type="button" onClick={submit}
                disabled={busy || !topic.trim()}
                data-testid="blog-brief-submit"
                className="hv-pill is-active">
          <Sparkles size={11} /> {busy ? "Drafting (15–30s)…" : "Draft post"}
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── */
/* Editor                                                        */
/* ───────────────────────────────────────────────────────────── */
function BlogEditor({ post, token, onUpdated, onDeleted }) {
  const [title, setTitle] = useState(post.title);
  const [excerpt, setExcerpt] = useState(post.excerpt || "");
  const [category, setCategory] = useState(post.category || "industry");
  const [tags, setTags] = useState((post.tags || []).join(", "));
  const [readMinutes, setReadMinutes] = useState(post.read_minutes || 4);
  const [body, setBody] = useState(post.body_html || "");
  const [refs, setRefs] = useState((post.references || []).join("\n"));
  const [cover, setCover] = useState(post.cover || "");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  // Sync local state when a different post is selected (or revisions restored)
  useEffect(() => {
    setTitle(post.title);
    setExcerpt(post.excerpt || "");
    setCategory(post.category || "industry");
    setTags((post.tags || []).join(", "));
    setReadMinutes(post.read_minutes || 4);
    setBody(post.body_html || "");
    setRefs((post.references || []).join("\n"));
    setCover(post.cover || "");
  }, [post.id, post.updated_at]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (note) => {
    setBusy("save"); setErr("");
    try {
      await studioFetch(`/api/content-studio/posts/${post.id}`, {
        method: "PATCH", token,
        body: {
          title, excerpt, category,
          tags: tags.split(/\s*,\s*/).filter(Boolean),
          read_minutes: parseInt(readMinutes, 10) || 4,
          body_html: body, cover,
          references: refs.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
          revision_note: note || "Manual edit",
        },
      });
      setSavedAt(Date.now());
      onUpdated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  const refine = async (instruction) => {
    setBusy("refine"); setErr("");
    try {
      // Persist any in-flight edits first so the LLM refines what the user sees
      await save("Auto-save before refine");
      await studioFetch(`/api/content-studio/posts/${post.id}/refine`, {
        method: "POST", token, body: { instruction },
      });
      onUpdated();
    } catch (e) { setErr(e.message); throw e; }
    finally { setBusy(""); }
  };

  const restore = async (revId) => {
    if (!window.confirm("Restore this revision? Current state will be snapshotted as 'Pre-restore' so you can come back.")) return;
    setBusy("restore"); setErr("");
    try {
      await studioFetch(
        `/api/content-studio/posts/${post.id}/revisions/${revId}/restore`,
        { method: "POST", token },
      );
      onUpdated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  const publish = async () => {
    if (!window.confirm(
      `Publish "${title}" to /insights now? It will appear on the live website immediately.`,
    )) return;
    setBusy("publish"); setErr("");
    try {
      await save("Save before publish");
      await studioFetch(`/api/content-studio/posts/${post.id}/publish`, {
        method: "POST", token,
      });
      onUpdated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  const unpublish = async () => {
    if (!window.confirm("Unpublish this post? It will disappear from /insights.")) return;
    setBusy("unpublish"); setErr("");
    try {
      await studioFetch(`/api/content-studio/posts/${post.id}/unpublish`, {
        method: "POST", token,
      });
      onUpdated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid={`blog-editor-${post.id}`}>
      {/* Header bar */}
      <div className="hv-card-soft flex items-center justify-between gap-3 flex-wrap"
           style={{ padding: 14 }}>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={post.status} />
          <span className="font-mono uppercase"
                style={{ fontSize: "0.62rem", letterSpacing: "0.18em",
                         color: "rgba(12,42,64,0.55)" }}>
            /insights/{post.slug}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => save()}
                  disabled={!!busy}
                  data-testid="blog-save"
                  className="hv-pill is-active">
            {busy === "save" ? "Saving…" : "Save"}
          </button>
          {savedAt && (
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.58rem", letterSpacing: "0.16em",
                           color: "rgba(12,42,64,0.55)" }}>
              saved
            </span>
          )}
          {post.status === "published" ? (
            <button type="button" onClick={unpublish}
                    disabled={!!busy}
                    data-testid="blog-unpublish"
                    className="hv-pill">
              <EyeOff size={11} /> Unpublish
            </button>
          ) : (
            <button type="button" onClick={publish}
                    disabled={!!busy}
                    data-testid="blog-publish"
                    className="hv-pill is-active"
                    style={{ background: "#0E7A82", color: "#FFFFFF",
                             borderColor: "#0E7A82" }}>
              <Eye size={11} /> Publish to /insights
            </button>
          )}
          <a href={`/insights/${post.slug}`} target="_blank" rel="noreferrer"
             className="hv-pill"
             data-testid="blog-preview"
             style={{ textDecoration: "none" }}>
            <ExternalLink size={11} /> Preview
          </a>
          <button type="button" onClick={onDeleted}
                  disabled={!!busy}
                  data-testid="blog-delete"
                  className="hv-pill is-danger">
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      {err && (
        <div className="px-4 py-3"
             style={{ background: "rgba(184,92,56,0.10)",
                      border: "1px solid rgba(184,92,56,0.35)",
                      color: "#B85C38", fontSize: "0.85rem",
                      borderRadius: 10 }}
             data-testid="blog-editor-error">
          {err}
        </div>
      )}

      {/* Editor card */}
      <div className="hv-card-soft" style={{ padding: 18 }}>
        <BriefField label="Title">
          <MicField value={title} onChange={setTitle} apiToken={token}
                    testid={`blog-edit-title-mic-${post.id}`}>
            <input type="text" value={title}
                   onChange={(e) => setTitle(e.target.value)}
                   data-testid="blog-edit-title"
                   className="hv-input w-full"
                   style={{ fontSize: "1.05rem", fontWeight: 600 }} />
          </MicField>
        </BriefField>

        <BriefField label="Excerpt (shown on /insights tiles)">
          <MicField value={excerpt} onChange={setExcerpt} apiToken={token}
                    testid={`blog-edit-excerpt-mic-${post.id}`}>
            <textarea value={excerpt}
                      onChange={(e) => setExcerpt(e.target.value)}
                      rows={2}
                      data-testid="blog-edit-excerpt"
                      className="hv-input w-full"
                      style={{ resize: "vertical" }} />
          </MicField>
        </BriefField>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BriefField label="Category">
            <select value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="blog-edit-category"
                    className="hv-input w-full">
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </BriefField>
          <BriefField label="Read minutes">
            <input type="number" min="1" max="60"
                   value={readMinutes}
                   onChange={(e) => setReadMinutes(e.target.value)}
                   data-testid="blog-edit-read-minutes"
                   className="hv-input w-full" />
          </BriefField>
          <BriefField label="Cover image (path or URL, optional)">
            <input type="text" value={cover}
                   onChange={(e) => setCover(e.target.value)}
                   data-testid="blog-edit-cover"
                   className="hv-input w-full"
                   placeholder="/generated/hero.webp" />
          </BriefField>
        </div>

        <BriefField label="Tags (comma-separated)">
          <input type="text" value={tags}
                 onChange={(e) => setTags(e.target.value)}
                 data-testid="blog-edit-tags"
                 className="hv-input w-full" />
        </BriefField>

        <BriefField label="Body — HTML (use <p>, <h3>, <ol>, <ul>, <li>)">
          <MicField value={body} onChange={setBody} apiToken={token}
                    testid={`blog-edit-body-mic-${post.id}`}>
            <textarea value={body}
                      onChange={(e) => setBody(e.target.value)}
                      rows={20}
                      data-testid="blog-edit-body"
                      className="hv-input w-full font-mono"
                      style={{ resize: "vertical", fontSize: "0.82rem",
                               lineHeight: 1.55 }} />
          </MicField>
        </BriefField>

        <BriefField label="References (one per line)">
          <textarea value={refs}
                    onChange={(e) => setRefs(e.target.value)}
                    rows={4}
                    data-testid="blog-edit-refs"
                    className="hv-input w-full"
                    style={{ resize: "vertical", fontSize: "0.85rem" }} />
        </BriefField>
      </div>

      <RefineBar onRefine={refine}
                 busy={busy === "refine"}
                 apiToken={token}
                 testidPrefix="blog" />

      <RevisionDrawer revisions={post.revisions}
                      onRestore={restore}
                      busy={!!busy}
                      testidPrefix="blog" />

      {/* Live preview block */}
      <details className="hv-card-soft" style={{ padding: 14 }}>
        <summary className="font-mono uppercase cursor-pointer"
                 style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                          color: "#0E7A82", fontWeight: 700 }}
                 data-testid="blog-preview-toggle">
          <RefreshCw size={12} style={{ display: "inline", marginRight: 6 }} />
          Render preview (read-only HTML)
        </summary>
        <article className="mt-3 prose"
                 style={{ maxWidth: 720, color: "#0C2A40",
                          lineHeight: 1.7, fontSize: "0.95rem" }}
                 data-testid="blog-html-preview"
                 dangerouslySetInnerHTML={{ __html: body }} />
      </details>
    </div>
  );
}
